import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../utils/api';

const SessionContext = createContext();

export const SessionProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  const clearSession = () => {
    setUser(null);
    setRoles([]);
    localStorage.removeItem('user');
    localStorage.removeItem('roles');
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    const storedRoles = localStorage.getItem('roles');

    if (storedUser && storedRoles) {
      try {
        setUser(storedUser);
        setRoles(JSON.parse(storedRoles));
        setLoading(false);
        return;
      } catch {
        clearSession();
      }
    }

    apiFetch('/api/me')
      .then(res => {
        if (!res.ok) throw new Error();
        return res.json();
      })
      .then(data => {
        setUser(data.username);
        setRoles(data.roles);
        localStorage.setItem('user', data.username);
        localStorage.setItem('roles', JSON.stringify(data.roles));
      })
      .catch(clearSession)
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback((username, roles) => {
    setUser(username);
    setRoles(roles);
    localStorage.setItem('user', username);
    localStorage.setItem('roles', JSON.stringify(roles));
  }, []);

  const logout = useCallback(() => {
    apiFetch('/api/logout', { method: 'POST' }).catch(() => {});
    clearSession();
    queryClient.clear();
  }, []);

  const hasRole = useCallback((required) => {
    const requiredRoles = Array.isArray(required) ? required : [required];
    return requiredRoles.some(role =>
      roles.some(r => r === role || r?.name === role)
    );
  }, [roles]);

  return (
    <SessionContext.Provider value={{
      user,
      roles,
      loading,
      authenticated: !!user,
      hasRole,
      login,
      logout
    }}>
      {children}
    </SessionContext.Provider>
  );
};

export const useSession = () => useContext(SessionContext);
